import { createFileRoute, Link } from '@tanstack/react-router'
import { UnlinkIcon } from 'lucide-react'
import { Panel } from '@/components/panel'
import { type BacklinkProfile, useBacklinkProfile } from '@/lib/backlinks'
import { formatExact, shortenUrl } from '@/lib/format'
import { useSiteScope } from '../../-section'
import { DomainFacet } from '../-facet'

export const Route = createFileRoute('/_app/$org/$host/backlinks/_list/dead')({
  component: DeadLinksPage,
})

/**
 * Links someone else made that now point at nothing here.
 *
 * Two grains on one page on purpose: the table is the domains, because that is
 * what the rest of the section counts and what the sidebar's number means, and
 * the panel under it is the individual links, because a dead target is only
 * actionable as a url. Every site gets these whether or not link-level
 * collection is on — the filtered slice is small enough to afford.
 */
function DeadLinksPage() {
  const { org, host, site } = useSiteScope()
  const { data } = useBacklinkProfile(site)

  return (
    <>
      <DomainFacet
        title="Pointing at dead urls"
        status="broken"
        emptyLabel="No linking domain points at a dead url here."
      />

      {data && data.broken.total > 0 && <BrokenPanel data={data} org={org} host={host} />}
    </>
  )
}

function BrokenPanel({ data, org, host }: { data: BacklinkProfile; org: string; host: string }) {
  return (
    <Panel title="The links themselves" meta={formatExact(data.broken.total)} flush>
      <ul className="divide-y divide-border">
        {data.broken.rows.map((link) => (
          <li key={link.id} className="flex items-baseline gap-3 px-4 py-2.5 text-[12.5px]">
            <UnlinkIcon className="size-3.5 shrink-0 self-center text-destructive" aria-hidden />
            <div className="min-w-0 flex-1">
              <p className="truncate font-mono" title={link.urlFrom}>
                {link.domain}
              </p>
              <p
                className="truncate font-mono text-[11.5px] text-muted-foreground"
                title={link.urlTo}
              >
                → {shortenUrl(link.urlTo, host)}
              </p>
            </div>
            <span className="shrink-0 font-mono text-[11px] text-muted-foreground tabular">
              {link.urlToStatusCode ?? '—'}
            </span>
            {link.pageId && (
              <Link
                to="/$org/$host/pages/$pageId"
                params={{ org, host, pageId: String(link.pageId) }}
                className="mono-label shrink-0 text-muted-foreground transition-colors hover:text-foreground"
              >
                Page
              </Link>
            )}
          </li>
        ))}
      </ul>
    </Panel>
  )
}
